import Hotjar from '@hotjar/browser'
import { LOCAL_STORAGE_get_ga, LOCAL_STORAGE_set_ga } from '@localStorage/googleAnalytics.localStorage'
import * as Sentry from '@sentry/react'
import { generateConsentContext } from 'parkdepot-shared/utils/consentContextGenerator'
import React from 'react'
import ReactGA from 'react-ga4'
import TagManager from 'react-gtm-module'
import { useImmer } from 'use-immer'

const INITIAL_PERMISSIONS = {
    allowHotjar: false,
    allowSentry: false,
    allowGoogleAnalytics: false,
    allowGoogleMaps: false,
    allowGoogleTag: false,
    hasDecided: false
}

interface IPermissions {
    allowHotjar: boolean
    allowSentry: boolean
    allowGoogleAnalytics: boolean
    allowGoogleMaps: boolean
    allowGoogleTag: boolean
    hasDecided: boolean
}

interface IPermissionsDetails {
    Hotjar?: boolean
    Sentry?: boolean
    'Google Analytics'?: boolean
    'Google Maps'?: boolean
    'Google Tag Manager'?: boolean
    action: string
}

const [ConsentContext] = generateConsentContext<IPermissions, IPermissionsDetails>()

export const BookingConsentContainer: React.FC = ({ children }) => {
    const [permissions, setPermission] = useImmer(INITIAL_PERMISSIONS)

    React.useEffect(() => {
        // By now is any since we usercentric seems to not support TS
        const handlePermissions = (e: any) => {
            if (!e.detail || e.detail.event !== 'consent_status') return

            // true
            if (process.env.VITE_ENVIRONMENT && process.env.NODE_ENV && process.env.NODE_ENV !== 'development') {
                if (!Sentry.isInitialized()) {
                    Sentry.init({
                        dsn: process.env?.VITE_SENTRY_DSN,
                        environment: process.env?.VITE_ENVIRONMENT,
                        replaysOnErrorSampleRate: 1.0,
                        // replaysSessionSampleRate default is 0.1
                        replaysSessionSampleRate: 0,
                        integrations: (integrations) => [...integrations, Sentry.replayIntegration()]
                    })
                }

                const sentryClient = Sentry.getClient()
                if (sentryClient) {
                    sentryClient.getOptions().enabled = e.detail.Sentry
                }

                // check for consent status of Hotjar
                if (e.detail.Hotjar === true) {
                    const siteId = process.env.VITE_HOTJAR_SITEID
                    const hotjarVersion = 6
                    if (siteId) {
                        Hotjar.init(Number(siteId), hotjarVersion)
                    }
                } else if (Hotjar.isReady()) {
                    window.location.reload()
                }

                // Initialize GA only if it hasn't been initialized
                if (e.detail['Google Analytics'] === true) {
                    const gaId = process.env.VITE_GOOGLE_ANALYTICS_ID
                    if (gaId && !ReactGA.isInitialized) {
                        ReactGA.initialize(gaId)
                        LOCAL_STORAGE_set_ga(true)
                    }
                } else {
                    if (LOCAL_STORAGE_get_ga() && ReactGA.isInitialized) {
                        ReactGA.ga('set', 'sendHitTask', null) // Disable tracking
                        LOCAL_STORAGE_set_ga(false)
                    }
                }

                // Initialize GTM only if it hasn’t been initialized before
                if (e.detail['Google Tag Manager'] === true) {
                    if (process.env.VITE_GOOGLE_GTM_ID && !window.google_tag_manager) {
                        const gtmId = process.env.VITE_GOOGLE_GTM_ID
                        TagManager.initialize({
                            gtmId,
                            dataLayer: {
                                consentGranted: true,
                                google_ads_consent: e.detail['Google Ads'] === true ? 'granted' : 'denied'
                            }
                        })
                    }
                } else {
                    // Push consent revoked event to dataLayer
                    TagManager.dataLayer({
                        dataLayer: {
                            consentGranted: false,
                            google_ads_consent: 'denied'
                        }
                    })
                }
            }

            // set permissions
            setPermission((draft) => {
                draft.allowHotjar = !!e.detail?.Hotjar
                draft.allowSentry = !!e.detail?.Sentry
                draft.allowGoogleAnalytics = !!e.detail?.['Google Analytics']
                draft.allowGoogleMaps = !!e.detail?.['Google Maps']
                draft.allowGoogleTag = !!e.detail?.['Google Tag Manager']
                if (e.detail.event === 'consent_status' && e.detail.type === 'explicit') {
                    draft.hasDecided = true
                }
            })
        }

        window.addEventListener('ucEvent', handlePermissions)

        return () => {
            window.removeEventListener('ucEvent', handlePermissions)
        }
    }, [setPermission])

    return <ConsentContext.Provider value={permissions}>{children}</ConsentContext.Provider>
}

export { ConsentContext }
